import { z } from 'zod';
import { ACCOUNT_PROJECT_COMMAND_PAYLOADS } from './account-projects.js';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { AUTO_ARTIFACT_COMMAND_PAYLOADS } from './auto-artifacts.js';
import { BARK_COMMAND_PAYLOADS } from './bark.js'; // bark-triage
import { CALL_COMMAND_PAYLOADS, CallVia } from './call.js'; // viko-call
import { Agent, Id } from './common.js';
import { DECISION_COMMAND_PAYLOADS } from './decision.js'; // phase2-decisions
import { ApprovalDecision } from './events.js';
import { LandGrantProof } from './land-grant.js'; // land-grant
import { PRESENCE_COMMAND_PAYLOADS } from './machine-presence.js'; // passkey-budget WP1
import { PREVIEW_COMMAND_PAYLOADS } from './preview.js'; // live-preview-tunnel
import { PrivateProjectBinding } from './private-project.js';
import { PROJECT_SETUP_COMMAND_PAYLOADS } from './project-setup.js'; // g5-setup
import { REF_COMMAND_PAYLOADS } from './refs.js';
import { USAGE_LIMIT_COMMAND_PAYLOADS } from './usage-limit.js'; // g14-resume
// account-projects: likewise re-exported here instead of from the hotspot index.ts.
export * from './account-projects.js';
// ---- bark-triage: bark items, commands and triage MCP tools (area file bark.ts) ----
export * from './bark.js';
// ---- end bark-triage ----
// ---- phase2-decisions: decision entity, commands and MCP tools (area file decision.ts) ----
export * from './decision.js';
// land-grant: standing landing grants (area file land-grant.ts), re-exported here likewise.
export * from './land-grant.js';
// ---- end phase2-decisions ----
// ---- g5-setup: learned tendencies, scoping and setup proposals (area file project-setup.ts) ----
export * from './project-setup.js';
// ---- end g5-setup ----
// ---- g14-resume: usage-limit parking (area file usage-limit.ts) ----
export * from './usage-limit.js';
// ---- end g14-resume ----
export * from './preview.js'; // live-preview-tunnel: preview commands, relay frames, tunnel payloads
// openable-refs: re-exported here so the hotspot index.ts stays untouched.
export * from './refs.js';
/**
 * Command kind registry: every command a client can send to a daemon, with its payload schema.
 * The kind string is what `CommandEnvelope.kind` carries and what risky-kind policies match.
 * Additive-only: new kinds may be added; a kind's payload may only gain optional fields.
 */
const AttachmentRef = z.object({
    encrypted: EncryptedArtifactRef.optional(),
    artifactId: Id,
    name: z.string().optional(),
    mime: z.string().optional(),
});
export const COMMAND_PAYLOADS = {
    'account.add': z.object({
        provider: z.enum(['claude', 'codex']),
        label: z.string().trim().min(1).max(120),
    }),
    'account.rename': z.object({ accountId: Id, label: z.string().trim().min(1).max(120) }),
    'account.enable': z.object({ accountId: Id, enabled: z.boolean() }),
    'account.status': z.object({ accountId: Id }),
    /** Launch the official CLI login for an isolated account on that machine (browser opens there).
     * Credentials are written by the CLI into the account's own profile and never leave the machine. */
    'account.login': z.object({ accountId: Id, cancel: z.boolean().optional() }),
    /** Retire an isolated account: hidden from routing and lists; its profile is kept on disk. */
    'account.remove': z.object({ accountId: Id }),
    /** Start a run (fresh or resuming a native session). `runId` is client-minted. */
    'run.start': z
        .object({
        runId: Id,
        agent: Agent,
        /** Machine-local provider account, covered by the signed payload. */
        accountId: Id.optional(),
        prompt: z.string(),
        cwd: z.string().optional(),
        projectId: z.string().optional(),
        workstreamId: z.string().optional(),
        taskId: z.string().optional(),
        attachments: z.array(AttachmentRef).optional(),
        model: z.string().optional(),
        effort: z.string().optional(),
        /** additive (g18): why this model was picked, shown in the run header ("Sonnet 5.5: …"). */
        modelReason: z.string().max(300).optional(),
        mode: z.string().optional(),
        resumeNativeSessionId: z.string().optional(),
        privateProject: PrivateProjectBinding.optional(),
    })
        .refine((p) => !p.privateProject || p.projectId === p.privateProject.snapshot.id, 'private project mismatch'),
    /** Send a user message. When the run is busy: `queue` (default) waits for the turn; otherwise steer. */
    'run.message': z.object({
        runId: Id,
        text: z.string(),
        attachments: z.array(AttachmentRef).optional(),
        queue: z.boolean().optional(),
        /** Echoed as `user.msg.messageId` / `user.msg.status.messageId`. */
        clientMessageId: z.string().optional(),
        /** additive (viko-call): sent from a call; History shows it "via call". */
        callId: CallVia.optional(),
    }),
    'run.interrupt': z.object({
        runId: Id,
        /** Also drop queued messages ("Stop means stop"). */
        cancelQueued: z.boolean().optional(),
    }),
    /** Answer a `question` event. */
    'run.answer': z.object({
        runId: Id,
        questionId: Id,
        answers: z.record(z.string(), z.array(z.string())),
        notes: z.record(z.string(), z.string()).optional(),
    }),
    /** Resolve an `approval` event. */
    'run.approve': z.object({
        runId: Id,
        approvalId: Id,
        decision: ApprovalDecision,
        message: z.string().optional(),
        updatedInput: z.unknown().optional(),
    }),
    'run.set_model': z.object({ runId: Id, model: z.string(), effort: z.string().optional() }),
    'run.set_mode': z.object({ runId: Id, mode: z.string() }),
    /** Merge a workstream to main (or open the PR recipe). Risky → passkey step-up. */
    'land.merge': z.object({
        workstreamId: Id,
        runId: z.string().optional(),
        branch: z.string().optional(),
        target: z.enum(['main', 'pr']).optional(),
        /** additive (auto-land): tolerate recipe steps that fail on the target too ("land anyway"). */
        acceptBaselineFailures: z.boolean().optional(),
        /**
         * additive (land-grant): a standing landing grant standing in for the step-up tap. The daemon
         * checks it against its own project facts; never valid with `acceptBaselineFailures`.
         */
        landGrant: LandGrantProof.optional(),
        /** additive (viko-call): sent from a call; History shows it "via call". */
        callId: CallVia.optional(),
        /**
         * additive (g18-stack): the branch HEAD this approval is for. The daemon refuses the landing
         * when the branch has moved since (approve again), and re-checks it right before merging.
         */
        headSha: z
            .string()
            .regex(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/, 'a full commit id')
            .optional(),
    }),
    /** additive (auto-land): cancel a queued/running landing (nothing is published after it). */
    'land.cancel': z.object({ landingId: Id }),
    /** Ship a build through a release lane. Risky → passkey step-up. */
    'land.deploy': z.object({
        projectId: Id,
        lane: z.string(),
        ref: z.string().optional(),
        /** additive (viko-call): sent from a call; History shows it "via call". */
        callId: CallVia.optional(),
    }),
    /** Approve a `viko_run_with_secret` request. Risky → passkey step-up. */
    'secret.approve': z.object({
        requestId: Id,
        /** additive (passkey-budget WP0): `allow_always` keeps the old run.approve choice. */
        decision: z.enum(['allow', 'allow_always', 'deny']),
    }),
    // --- additive (v0): daemon maintenance kinds ------------------------------------------------
    /** Stop a run's process (resumable later with `run.start{resumeNativeSessionId}`). */
    'run.stop': z.object({ runId: Id, callId: CallVia.optional() }),
    /** Read-only hygiene report (orphan worktrees, merged-unpruned and stale branches). */
    'hygiene.scan': z.object({
        projectId: z.string().optional(),
        /** Explicit repo path on the target machine (instead of the project's). */
        repoPath: z.string().optional(),
        staleDays: z.number().int().positive().optional(),
    }),
    /** Run `viko doctor` now; the result is returned and reported via `machine.status`. */
    'doctor.run': z.object({ only: z.array(z.string()).optional() }),
    /**
     * Apply a doctor Fix the user accepted in the app (allowlisted checks only, e.g.
     * `macos.power.ac-sleep` → admin prompt on the Mac, `windows.power` → powercfg).
     */
    'doctor.fix': z.object({ checkId: z.string() }),
    /**
     * Brain triage on the brain machine (`@viko/brain` runTriage). Accepted immediately; the
     * outcome arrives as a `brain.result` frame with the same `requestId`.
     */
    'brain.triage': z.object({
        requestId: Id,
        /** `@viko/brain` CaptureInput `{id, text, attachments?, target?, origin?}`. */
        capture: z.looseObject({ id: Id, text: z.string() }),
        /** `@viko/brain` TriageContext `{catalog, workstreams, tasks}`. */
        context: z.looseObject({
            catalog: z.array(z.unknown()),
            workstreams: z.array(z.unknown()),
            tasks: z.array(z.unknown()),
        }),
        cardId: z.string().optional(),
    }),
    /** Brain retro (`@viko/brain` runRetro) over collected signals; result via `brain.result`. */
    'brain.retro': z.object({
        requestId: Id,
        /** `@viko/brain` RetroSignals. */
        signals: z.looseObject({ projects: z.array(z.unknown()) }),
        /** `@viko/brain` ProposalOptions minus `now` (+ `rules`). */
        options: z.record(z.string(), z.unknown()).optional(),
        /** Deterministic proposals only (no LLM call). */
        noLlm: z.boolean().optional(),
    }),
    // --- additive (add-project): register a repository on the target machine ----------------------
    /**
     * `viko project add <repoPath>` on the target machine. Without viko.project.yaml the daemon
     * returns an onboarding draft summary; `writeConfig` + that draft's `draftDigest` writes it.
     */
    'project.register': z.object({
        repoPath: z.string().trim().min(1).max(4096),
        writeConfig: z.boolean().optional(),
        draftDigest: z.string().max(64).optional(),
    }),
    // ---- openable-refs: resolve/read references mentioned in a transcript ----
    ...REF_COMMAND_PAYLOADS,
    // ---- end openable-refs ----
    // ---- auto-artifacts: publish an offered deliverable ----
    ...AUTO_ARTIFACT_COMMAND_PAYLOADS,
    // ---- end auto-artifacts ----
    // ---- account-projects: checkouts are machine-local; see account-projects.ts ----
    ...ACCOUNT_PROJECT_COMMAND_PAYLOADS,
    // ---- end account-projects ----
    // ---- bark-triage: bark.submit / bark.control (routed to the project's triage machine) ----
    ...BARK_COMMAND_PAYLOADS,
    // ---- end bark-triage ----
    // ---- phase2-decisions: decision.message / decision.answer (+ .risky for risky categories) ----
    ...DECISION_COMMAND_PAYLOADS,
    // ---- end phase2-decisions ----
    // ---- g5-setup: project.scope (scoping conversation before any checkout) ----
    ...PROJECT_SETUP_COMMAND_PAYLOADS,
    // ---- end g5-setup ----
    // ---- live-preview-tunnel: preview.open/close/share_port/unshare_port/shares ----
    ...PREVIEW_COMMAND_PAYLOADS,
    // ---- end live-preview-tunnel ----
    // ---- g14-resume: limit.resume_now / limit.switch_account ----
    ...USAGE_LIMIT_COMMAND_PAYLOADS,
    // ---- end g14-resume ----
    // ---- passkey-budget WP1: presence.unlock_proof (lock follows the OS) ----
    ...PRESENCE_COMMAND_PAYLOADS,
    // ---- end passkey-budget ----
    // ---- viko-call: call.start / call.utterance / call.audio / call.end (never risky) ----
    ...CALL_COMMAND_PAYLOADS,
    // ---- end viko-call ----
};
export const COMMAND_KINDS = Object.keys(COMMAND_PAYLOADS);
export const CommandKindSchema = z.enum(COMMAND_KINDS);
/**
 * Risky kind patterns (a trailing `x.*` matches the `x.` prefix). The protocol owns this list;
 * `@viko/crypto` DEFAULT_RISKY_KINDS must equal it (checked by a test).
 */
export const RISKY_KIND_PATTERNS = [
    'land.merge',
    'land.deploy',
    'release.*',
    'secret.*',
    'policy.*',
    'device.*',
    'credential.*',
    'daemon.break-glass',
    'daemon.break-glass.*',
    'work.delete-unmerged',
    // account-projects: clone/create (statement-approved) and machine-local policy edits.
    'project.checkout',
    'project.policy',
    // phase2-decisions: answering a secrets/security/release/irreversible decision.
    'decision.answer.risky',
    // live-preview-tunnel: letting previews reach a port the run does not own (writes to the host).
    'preview.share_port',
];
/**
 * passkey-budget WP0: a daemon refuses `run.approve` that would ALLOW a secret request (the
 * failure message starts with this code); the client re-sends it as the risky `secret.approve`.
 * Denying or cancelling a secret request stays passkey-free.
 */
export const SECRET_NEEDS_PASSKEY = 'secret-needs-passkey';
/**
 * passkey-budget WP6: a daemon refuses a `land.merge` under a standing grant whose branch changes
 * protected paths (secrets, CI, release config) with this code; the client re-sends it without
 * the grant, so it takes a passkey (the approval tray).
 */
export const GRANT_PROTECTED_PATH = 'grant-protected-path';
export function isRiskyCommandKind(kind, patterns = RISKY_KIND_PATTERNS) {
    return patterns.some((p) => (p.endsWith('.*') ? kind.startsWith(p.slice(0, -1)) : kind === p));
}
export function isCommandKind(kind) {
    return Object.hasOwn(COMMAND_PAYLOADS, kind);
}
/** Validate a payload against its kind's schema. */
export function parseCommandPayload(kind, payload) {
    if (!isCommandKind(kind))
        return { ok: false, reason: 'unknown-kind' };
    const r = COMMAND_PAYLOADS[kind].safeParse(payload);
    return r.success
        ? { ok: true, kind, payload: r.data }
        : { ok: false, reason: 'invalid-payload', error: r.error.message };
}
/** g18-stack: a full commit id (what a landing approval binds to). */
export function isFullSha(sha) {
    return !!sha && /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/.test(sha);
}
//# sourceMappingURL=commands.js.map